import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

// =============================================================================
// Quotation / RFQ task lifecycle (svc-06-quotation, via gateway /v1/tasks).
// Mirrors packages/shared-types/src/svc-06-quotation/v1/{task,quote-finalize,
// state-history}.schema.ts. Types kept inline (admin api/*.ts convention).
// =============================================================================

export type TaskFlowType = "engineer" | "sales";
export type MachineCostMode = "shared" | "dedicated";

export interface TaskSummary {
  taskId: string;
  status: string | null;
  flowType: TaskFlowType | null;
  machineCostMode: MachineCostMode | null;
  comment: string | null;
  createdBy: string | null;
  workingForUserId: string | null;
  workingForEmail: string | null;
  // Names on the summary so "Requested by" shows the user name.
  workingForFirstName: string | null;
  workingForLastName: string | null;
  requestedByEmail: string | null;
  requestedByFirstName: string | null;
  requestedByLastName: string | null;
  requestedByCompanyName: string | null;
  quotedAt: string | null;
  workflowEngReviewStatus: string | null;
  workflowQuoteStatus: string | null;
  hasSnapshot: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TaskDetail extends TaskSummary {
  workingForCompanyName: string | null;
  workingForPhone: string | null;
  workingForCompanyAddress: string | null;
  requestedByPhone: string | null;
  requestedByCompanyAddress: string | null;
  requestedByRole: string | null;
  quotedByEmail: string | null;
  quotedByName: string | null;
  supportResponses: Array<{
    response: string;
    respondedAt: string;
    responderName: string | null;
    responderEmail: string | null;
  }>;
}

export interface PaginatedTasks {
  items: TaskSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListTasksQuery {
  status?: string;
  flowType?: TaskFlowType;
  createdByEmail?: string;
  search?: string;
  quoted?: "true" | "false";
  assigned?: "true" | "false";
  limit?: number;
  offset?: number;
}

export interface TaskSnapshotSelectedOption {
  partNo: string;
  description: string | null;
  category: string | null;
  toolingType: string | null;
  machineType: string | null;
  itemName: string | null;
  partName: string | null;
  price: number | null;
  currency: string | null;
  /** Free text typed against the `__CUSTOM_TOOLING__` placeholder line. */
  requirements: string | null;
  /** Null only for snapshots created before quantity persistence shipped. */
  qty: number | null;
}

export interface TaskSnapshot {
  taskId: string;
  source: string | null;
  solutionProductId: string | null;
  solutionName: string | null;
  solutionMachineType: string | null;
  filterRegion: string | null;
  filterView: string | null;
  filterSize: string | null;
  filterPm: boolean | null;
  filterRi: boolean | null;
  filterForceMinKn: number | null;
  filterForceMaxKn: number | null;
  investmentCost: string | null;
  operatorCost: string | null;
  qualityCost: string | null;
  totalCost: string | null;
  costModelIdSnapshot: string | null;
  machineCostMode: MachineCostMode | null;
  selectedOptions: TaskSnapshotSelectedOption[];
  selectedPartNos: string[];
  filterTypes: string[];
  filterCompareIds: string[];
  costAvailable: boolean;
  costUnavailableReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FinalizeQuoteRequest {
  note?: string | null;
  recipientEmail?: string | null;
}

export interface FinalizeQuoteResponse {
  taskId: string;
  quotedAt: string;
  quotedByEmail: string | null;
  quotedByName: string | null;
  workflowQuoteStatus: string | null;
  notificationOutboxId: string | null;
  notificationRecipient: string | null;
}

export interface TaskStateHistoryEntry {
  id: string;
  taskId: string;
  flowType: TaskFlowType;
  eventType: string;
  fromStatus: string | null;
  toStatus: string;
  stepName: string | null;
  stepOrder: number | null;
  actorUserId: string | null;
  actorEmail: string;
  actorName: string | null;
  actorRoleCode: string | null;
  decisionReason: string | null;
  occurredAt: string;
}

export interface PaginatedTaskStateHistory {
  items: TaskStateHistoryEntry[];
  total: number;
  limit: number;
  offset: number;
}

/** A frozen application on a task — `savedId` is the live application UUID. */
export interface TaskApplicationLink {
  appId: string;
  name: string | null;
  applicationNumber: string | null;
  oem: string | null;
  productionProgram: string | null;
  savedId: string | null;
}

function toQuery(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** GET /v1/tasks — internal region-scoped queue (sales/eng/admin). */
export async function listTasks(
  query: ListTasksQuery = {},
): Promise<PaginatedTasks> {
  const res = await Api.get(
    `/v1/tasks${toQuery(query as Record<string, unknown>)}`,
  );
  return unwrap<PaginatedTasks>(res.data);
}

/** GET /v1/tasks/:taskId. */
export async function getTask(taskId: string): Promise<TaskDetail> {
  const res = await Api.get(`/v1/tasks/${encodeURIComponent(taskId)}`);
  return unwrap<TaskDetail>(res.data);
}

/** GET /v1/tasks/:taskId/snapshot — null when no snapshot yet. */
export async function getSnapshot(
  taskId: string,
): Promise<TaskSnapshot | null> {
  const res = await Api.get(
    `/v1/tasks/${encodeURIComponent(taskId)}/snapshot`,
    {
      validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
    },
  );
  if (res.status === 404) return null;
  return unwrap<TaskSnapshot>(res.data);
}

/** DELETE /v1/tasks/:taskId — admin hard delete. */
export async function deleteTask(taskId: string): Promise<void> {
  await Api.delete(`/v1/tasks/${encodeURIComponent(taskId)}`);
}

export interface UpdateTaskBody {
  comment?: string | null;
  status?: string | null;
  flowType?: TaskFlowType;
  machineCostMode?: MachineCostMode | null;
}

export interface RespondAndCloseTaskResponse {
  taskId: string;
  status: "quoted";
  response: string;
  respondedAt: string;
  respondedBy: string;
  quotedAt: string;
}

/** PATCH /v1/tasks/:taskId — partial update of mutable task fields. */
export async function updateTask(
  taskId: string,
  body: UpdateTaskBody,
): Promise<TaskDetail> {
  const res = await Api.patch(`/v1/tasks/${encodeURIComponent(taskId)}`, body);
  return unwrap<TaskDetail>(res.data);
}

/** POST /v1/tasks/:taskId/respond-and-close — message-only support tasks. */
export async function respondAndCloseTask(
  taskId: string,
  response: string,
): Promise<RespondAndCloseTaskResponse> {
  const res = await Api.post(
    `/v1/tasks/${encodeURIComponent(taskId)}/respond-and-close`,
    { response },
  );
  return unwrap<RespondAndCloseTaskResponse>(res.data);
}

/** POST /v1/tasks/:taskId/quote — finalize (svc-07 gated: 409 awaiting-approval). */
export async function finalizeQuote(
  taskId: string,
  body: FinalizeQuoteRequest = {},
): Promise<FinalizeQuoteResponse> {
  const res = await Api.post(
    `/v1/tasks/${encodeURIComponent(taskId)}/quote`,
    body,
  );
  return unwrap<FinalizeQuoteResponse>(res.data);
}

/**
 * GET /v1/tasks/:taskId/applications — the task's frozen applications. Used to
 * surface per-application attachments on the task detail.
 */
export async function getTaskApplications(
  taskId: string,
): Promise<TaskApplicationLink[]> {
  const res = await Api.get(
    `/v1/tasks/${encodeURIComponent(taskId)}/applications`,
  );
  return unwrap<{ items: TaskApplicationLink[] }>(res.data).items;
}

/** GET /v1/tasks/:taskId/state-history. */
export async function getStateHistory(
  taskId: string,
  query: { limit?: number; offset?: number } = {},
): Promise<PaginatedTaskStateHistory> {
  const res = await Api.get(
    `/v1/tasks/${encodeURIComponent(taskId)}/state-history${toQuery(query as Record<string, unknown>)}`,
  );
  return unwrap<PaginatedTaskStateHistory>(res.data);
}
